import { createContext, useContext, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

// CEO Rev 1.0 — Phase 1, S3. The campaign you've ENTERED. Every workspace tab
// reads campaignId from here instead of a dropdown, so a tab can only ever show
// one campaign's data.
export type WorkspaceCampaign = {
  id: string;
  name: string;
  publicName: string | null;
  industry: string | null;
  status: string;
  consentTier: string;
  ownerUserId: string;
  persona?: string;
  defaultWelcomeMessage?: string | null;
};

type WorkspaceCtx = { campaign: WorkspaceCampaign; campaignId: string };

const WorkspaceContext = createContext<WorkspaceCtx | null>(null);

export function useWorkspace(): WorkspaceCtx {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error("useWorkspace outside <WorkspaceProvider>");
  return ctx;
}

// Role-aware UI capabilities for the ENTERED campaign (board #2504/#2505/#2506:
// every role saw every button; the server rejected the write but QA rightly
// read visible controls as "access"). This mirrors the authoritative matrix in
// server/src/auth/can.ts — the server remains the enforcer; keep the two in
// sync when the matrix changes:
//   survey.manage        super_admin | owning campaign_owner | people_ops
//   workspaceItem.manage super_admin | owning campaign_owner
//   campaign.assignAssociate (Team)  super_admin | owning campaign_owner | people_ops
//   playlist.create      super_admin | campaign_owner | ae
//   issuance.send        super_admin | ae | people_ops
export function useWorkspaceCan() {
  const { user } = useAuth();
  const { campaign } = useWorkspace();
  const admin = user?.role === "super_admin";
  const owns = user?.role === "campaign_owner" && campaign.ownerUserId === user.id;
  return {
    manageSurveys: admin || owns || user?.role === "people_ops",
    manageItems: admin || owns,
    manageTeam: admin || owns || user?.role === "people_ops",
    managePlaylists: admin || owns || user?.role === "ae",
    sendPlaylist: admin || user?.role === "ae" || user?.role === "people_ops",
    // video.upload is role-broad (content_producer uploads anywhere) but a
    // campaign_owner may only upload INTO a campaign they own.
    uploadMedia: admin || owns || user?.role === "content_producer",
  };
}

export function useWorkspaceCampaignQuery(id: string) {
  return useQuery({
    queryKey: ["workspace-campaign", id],
    queryFn: () => api.get<WorkspaceCampaign>(`/api/admin/campaigns/${id}`),
    enabled: !!id,
  });
}

export function WorkspaceProvider({
  campaign,
  children,
}: {
  campaign: WorkspaceCampaign;
  children: ReactNode;
}) {
  return (
    <WorkspaceContext.Provider value={{ campaign, campaignId: campaign.id }}>
      {children}
    </WorkspaceContext.Provider>
  );
}
